import axios from 'axios';
import backendUrl from '../store/config';

class ConnectionMonitor {
  private static instance: ConnectionMonitor;
  private isConnected: boolean = true;
  private listeners: Array<(isConnected: boolean) => void> = [];
  private checkInterval: NodeJS.Timeout | null = null;
  private lastFailureTime: number = 0;
  private consecutiveFailures: number = 0;

  private constructor() {}

  public static getInstance(): ConnectionMonitor {
    if (!ConnectionMonitor.instance) {
      ConnectionMonitor.instance = new ConnectionMonitor();
    }
    return ConnectionMonitor.instance;
  }

  public addListener(callback: (isConnected: boolean) => void): void {
    this.listeners.push(callback);
  }

  public removeListener(callback: (isConnected: boolean) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.isConnected));
  }

  public async checkConnection(): Promise<boolean> {
    try {
      // Use a simple health check endpoint or just check if the server responds
      await axios.get(`${backendUrl}/api/health`, {
        timeout: 5000,
        // Don't use the interceptor for this check to avoid infinite loops
        transformRequest: [(data) => data],
        transformResponse: [(data) => data],
      });
      
      if (this.consecutiveFailures > 0) {
        console.log('Connection restored to backend');
      }
      
      this.consecutiveFailures = 0;
      this.setConnectionStatus(true);
      return true;
    } catch (error) {
      this.consecutiveFailures++;
      
      // Only consider it a connection error after multiple failures
      if (this.consecutiveFailures >= 2) {
        console.error('Backend connection failed:', error);
        this.setConnectionStatus(false);
      }
      
      return false;
    }
  }

  private setConnectionStatus(connected: boolean): void {
    if (this.isConnected !== connected) {
      this.isConnected = connected;
      this.notifyListeners();
    }
  }

  public startMonitoring(intervalMs: number = 30000): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    // Initial check
    this.checkConnection();

    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      this.checkConnection();
    }, intervalMs);
  }

  public stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  public isCurrentlyConnected(): boolean {
    return this.isConnected;
  }

  // Method to be called when an API request fails
  public reportConnectionError(): void {
    const now = Date.now();
    // Only update status if this is a recent failure (within last 10 seconds)
    if (now - this.lastFailureTime < 10000) {
      this.consecutiveFailures++;
    } else {
      this.consecutiveFailures = 1;
    }
    
    this.lastFailureTime = now;
    
    if (this.consecutiveFailures >= 2) {
      this.setConnectionStatus(false);
    }
  }

  // Method to be called when an API request succeeds
  public reportConnectionSuccess(): void {
    if (this.consecutiveFailures > 0) {
      this.consecutiveFailures = 0;
      this.setConnectionStatus(true);
    }
  }
}

export default ConnectionMonitor;